/**
 * Address Validation Service (Frontend)
 * Validates South African addresses - mirrors backend validation logic
 */

const SA_PROVINCE_KEYS = new Set([
  "gauteng",
  "western cape",
  "eastern cape",
  "kwazulu-natal",
  "kwazulu natal",
  "kzn",
  "limpopo",
  "mpumalanga",
  "north west",
  "northwest",
  "northern cape",
  "free state",
  "freestate",
  "gp",
  "wc",
  "ec",
  "lp",
  "mp",
  "nw",
  "nc",
  "fs",
]);

function normalizeAddressKey(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[-–—]/g, " ")
    .replace(/\s+/g, " ");
}

function isSouthAfricanProvincePart(value) {
  const key = normalizeAddressKey(value);
  return Boolean(key) && SA_PROVINCE_KEYS.has(key);
}

function isPostalCodePart(value) {
  return /^\d{4}$/.test(String(value || "").trim());
}

function isCountryPart(value) {
  const key = normalizeAddressKey(value);
  return key === "south africa" || key === "za" || key === "rsa";
}

/**
 * Split comma parts into street / suburb / town / city / province.
 * Trailing country, province, and postal code are recognized from the end.
 * @param {string[]} parts
 * @returns {{ streetPart: string, suburb: string, town: string, city: string, postalCode: string, province: string }}
 */
export function extractAddressParts(parts) {
  const working = [...parts];
  if (working.length && isCountryPart(working[working.length - 1])) {
    working.pop();
  }
  let province = "";
  if (
    working.length &&
    isSouthAfricanProvincePart(working[working.length - 1])
  ) {
    province = working.pop();
  }
  let postalCode = "";
  if (working.length && isPostalCodePart(working[working.length - 1])) {
    postalCode = working.pop();
  }

  const suburb = working[1]?.trim() || "";
  const town = working[2]?.trim() || suburb;
  const city = working[3]?.trim() || town;

  return {
    streetPart: working[0]?.trim() || "",
    suburb,
    town,
    city,
    postalCode,
    province,
  };
}

export class AddressValidationService {
  /**
   * Parse address string into components
   * @param {string} addressString - Address string to parse
   * @returns {Object} Parsed address components
   */
  static parseAddress(addressString) {
    if (!addressString || typeof addressString !== "string") {
      return {
        isValid: false,
        error: "Address must be a non-empty string",
      };
    }

    // Normalize address: trim, remove extra spaces, handle missing spaces after abbreviations
    let normalized = addressString
      .trim()
      .replace(/\s+/g, " ") // Replace multiple spaces with single space
      .replace(/([A-Za-z])([A-Z])/g, "$1 $2") // Add space between DrBlairgowrie -> Dr Blairgowrie
      .replace(/,\s*,/g, ","); // Remove double commas

    // Split by comma
    const parts = normalized
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);

    if (parts.length < 3) {
      return {
        isValid: false,
        error: `Address must have at least 3 components separated by commas. Found ${parts.length} components.`,
        parts: parts,
      };
    }

    const { streetPart, suburb, town, city, postalCode, province } =
      extractAddressParts(parts);

    // Extract street number and name from first part
    const streetMatch = streetPart.match(/^(\d+)\s+(.+)$/);
    if (!streetMatch) {
      return {
        isValid: false,
        error:
          "First part of address must start with a street number followed by street name",
        streetPart: streetPart,
      };
    }

    const streetNumber = streetMatch[1];
    const streetName = streetMatch[2];

    // Validate required components (minimum: street number, street name, suburb, town/city)
    const missing = [];
    if (!streetNumber) missing.push("street number");
    if (!streetName) missing.push("street name");
    if (!suburb) missing.push("suburb/area name");
    if (!town) missing.push("town/city");

    if (missing.length > 0) {
      return {
        isValid: false,
        error: `Missing required address components: ${missing.join(", ")}`,
        missing: missing,
      };
    }

    // Validate province if provided (South African provinces)
    // Province is optional but if provided, must be valid
    if (province) {
      const validProvinces = [
        "gauteng",
        "western cape",
        "eastern cape",
        "kwazulu-natal",
        "kwazulu natal",
        "kzn",
        "limpopo",
        "mpumalanga",
        "north west",
        "northwest",
        "northern cape",
        "free state",
        "freestate",
      ];

      // Normalize province: lowercase, trim, and normalize hyphens/spaces
      const provinceLower = province.toLowerCase().trim();
      // Normalize hyphens and spaces - replace hyphens with spaces for comparison
      const provinceNormalized = provinceLower
        .replace(/[-–—]/g, " ")
        .replace(/\s+/g, " ");

      // Check if normalized input matches any valid province (check both directions)
      const isValidProvince = validProvinces.some((valid) => {
        const validNormalized = valid
          .replace(/[-–—]/g, " ")
          .replace(/\s+/g, " ");
        // Check if input includes valid OR valid includes normalized input
        return (
          provinceNormalized.includes(validNormalized) ||
          validNormalized.includes(provinceNormalized) ||
          provinceLower.includes(valid) ||
          valid.includes(provinceNormalized)
        );
      });

      if (!isValidProvince) {
        return {
          isValid: false,
          error: `Invalid province: ${province}. Must be a valid South African province.`,
          province: province,
        };
      }
    }

    return {
      isValid: true,
      streetNumber: streetNumber,
      streetName: streetName,
      suburb: suburb,
      town: town,
      city: city || town, // Use town as city if city not provided
      postalCode: postalCode || "",
      province: province ? this.normalizeProvince(province) : "",
      fullAddress: addressString,
      formatted: this.formatAddress({
        streetNumber,
        streetName,
        suburb,
        town,
        city: city || town,
        postalCode,
        province: province ? this.normalizeProvince(province) : "",
      }),
    };
  }

  /**
   * Validate address format
   * @param {string} addressString - Address string to validate
   * @returns {Object} Validation result
   */
  static validateAddress(addressString) {
    const parsed = this.parseAddress(addressString);

    if (!parsed.isValid) {
      return {
        isValid: false,
        error: parsed.error,
        details: parsed,
      };
    }

    return {
      isValid: true,
      address: parsed,
    };
  }

  /**
   * Normalize province name to standard format
   * @param {string} province - Province name
   * @returns {string} Normalized province name
   */
  static normalizeProvince(province) {
    const provinceLower = province.toLowerCase().trim();

    const provinceMap = {
      gauteng: "Gauteng",
      "western cape": "Western Cape",
      "eastern cape": "Eastern Cape",
      "kwazulu-natal": "KwaZulu-Natal",
      "kwazulu natal": "KwaZulu-Natal",
      kzn: "KwaZulu-Natal",
      limpopo: "Limpopo",
      mpumalanga: "Mpumalanga",
      "north west": "North West",
      northwest: "North West",
      "northern cape": "Northern Cape",
      "free state": "Free State",
      freestate: "Free State",
    };

    // Find matching province
    for (const [key, value] of Object.entries(provinceMap)) {
      if (provinceLower.includes(key)) {
        return value;
      }
    }

    // Return capitalized version if not found
    return province
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  /**
   * Format address components into standard string
   * @param {Object} components - Address components
   * @returns {string} Formatted address
   */
  static formatAddress(components) {
    const { streetNumber, streetName, suburb, town, city, postalCode, province } =
      components;
    const parts = [];
    const pushUnique = (value) => {
      if (!value) return;
      const normalized = String(value).trim();
      if (!normalized) return;
      const last = parts[parts.length - 1];
      if (last && last.toLowerCase() === normalized.toLowerCase()) return;
      parts.push(normalized);
    };

    pushUnique(`${streetNumber} ${streetName}`.trim());
    pushUnique(suburb);
    pushUnique(town);
    pushUnique(city);
    pushUnique(postalCode);
    pushUnique(province);
    return parts.join(", ");
  }

  /**
   * Validate both pickup and delivery addresses
   * @param {string} pickupAddress - Pickup address
   * @param {string} deliveryAddress - Delivery address
   * @returns {Object} Validation result for both addresses
   */
  static validateBookingAddresses(pickupAddress, deliveryAddress) {
    const pickupValidation = this.validateAddress(pickupAddress);
    const deliveryValidation = this.validateAddress(deliveryAddress);

    const errors = [];
    if (!pickupValidation.isValid) {
      errors.push(`Pickup address: ${pickupValidation.error}`);
    }
    if (!deliveryValidation.isValid) {
      errors.push(`Delivery address: ${deliveryValidation.error}`);
    }

    return {
      isValid: pickupValidation.isValid && deliveryValidation.isValid,
      pickup: pickupValidation,
      delivery: deliveryValidation,
      errors: errors.length > 0 ? errors : null,
    };
  }
}
